import { useCallback, useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { PlayerSheetData, PlayerSheetTarget } from './playerSheetTypes';

export const usePlayerSheetData = (target: PlayerSheetTarget | null, readOnly = false) => {
  const [data, setData] = useState<PlayerSheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const variant = target?.variant;
  const clientId = target?.clientId;
  const seasonId = target?.seasonId;
  const playerSeasonId = target?.playerSeasonId;
  const playerId = target?.playerId;
  const enabled = !!target;

  const loadSheet = useCallback(async () => {
    if (!enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const resolvedClientId = clientId || sessionManager.getClientId();
    const resolvedSeasonId = seasonId || sessionManager.getSeasonId();
    const gameMode = sessionManager.getGamesMode();
    const isCareer = variant === 'career' && !!playerId;
    if (!resolvedClientId || (isCareer ? !playerId : !resolvedSeasonId || !playerSeasonId)) {
      setError('Missing player or season context');
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = readOnly ? `${API_URLS.BASE_URL}/share/players/sheet` : API_URLS.GET_PLAYER_STAT_SHEET;
      const params = isCareer
        ? { clientId: resolvedClientId, playerId, gameMode, ...(resolvedSeasonId ? { seasonId: resolvedSeasonId } : {}) }
        : { clientId: resolvedClientId, seasonId: resolvedSeasonId, playerSeasonId, gameMode };
      const response = await api.get(url, { params });
      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.message || 'Failed to load player sheet');
      }
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load player sheet');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, variant, clientId, seasonId, playerSeasonId, playerId, readOnly]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  return { data, loading, error, reload: loadSheet };
};
